import { Button, FormGroup } from "@mui/material";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { useEffect, useState } from "react";
import * as Yup from "yup";
import {
  getBlockData,
  getCurrentUser,
  getFloorData,
  getRoomType,
  insertRoomData,
} from "../../api-config/config";
import Snackbar from "../SnackBar/SnackBar";

const NewAddRoomForm = (props: any) => {
  const { handleClose, onRoomAdded, roomData, showSnackbarWithCallback } = props;
  const [roomTypeList, setRoomTypeList] = useState<any[]>([]);
  const [floorList, setfloorList] = useState<any[]>([]);
  const [selectedBlockId, setSelectedBlockId] = useState(roomData?.block_id);
  const [selectedFloorId, setSelectedFloorId] = useState(roomData?.floor_id);
  const [blockList, setBlockList] = useState<any[]>([]);
  const loggedUser = getCurrentUser();

  const [mode, setMode] = useState<"single" | "bulk">("single");

  const [snackbar, setSnackbar] = useState({
    visible: false,
    message: "",
    type: "success" as "success" | "error" | "warning" | "info",
  });

  const showSnackbar = (message: string, type: any = "success") => {
    setSnackbar({ visible: true, message, type });
  };

  const getRoomTypeList = (hotel_code: number) => {
    return getRoomType(hotel_code).then((res: any) => {
      if (res?.status_message === "success") {
        setRoomTypeList(res.data || []);
      }
    });
  };

  useEffect(() => {
    getRoomTypeList(loggedUser?.hotel_code);
  }, []);

  const defaultValues = {
    room_type_id: roomData?.room_type_id || "",
    room_number: roomData?.room
      ? Number(roomData?.room?.split(" ").pop())
      : "",
    room_heading: roomData?.room?.split(" ")[0] || "",
    description: roomData?.description || "",
    bulkStart: "",
    bulkCount: "",
  };

  const formValidation = Yup.object().shape({
    room_type_id: Yup.string().required("Please select Room type"),
    room_heading: Yup.string().required("Please enter Room Heading"),
    room_number: mode === "single"
      ? Yup.number()
          .typeError("Room number must be a number")
          .required("Please enter Room number")
      : Yup.string(),
    bulkStart: mode === "bulk"
      ? Yup.number()
          .typeError("Start Room number must be a number")
          .required("Please enter Start Room number")
      : Yup.string(),
    bulkCount: mode === "bulk"
      ? Yup.number()
          .typeError("Number of Rooms must be a number")
          .min(1, "At least 1 room must be added")
          .required("Please enter Number of Rooms")
      : Yup.string(),
  });

  const handleSubmit = async (formData: any) => {
    const basePayload = {
      id: roomData?.room_id,
      room_heading: formData.room_heading,
      hotel_code: loggedUser?.hotel_code,
      room_type_id: formData.room_type_id,
      floor_id: Number(selectedFloorId),
      block_id: Number(selectedBlockId),
      description: formData.description,
    };

    if (!basePayload.block_id || !basePayload.floor_id) {
      showSnackbar("Please Select Block and Floor", "error");
      return;
    }

    try {
      if (mode === "single") {
        const res = await insertRoomData({
          ...basePayload,
          room_number: formData.room_number,
        });

        if (res?.status_message === "failed") {
          showSnackbar(res.message || "Failed to Add Room", "error");
          return;
        }

        if (showSnackbarWithCallback) {
          showSnackbarWithCallback("Room Added successfully", "success", () => {
            onRoomAdded();
          });
          handleClose();
        } else {
          handleClose();
          onRoomAdded();
        }
        return;
      }

      if (mode === "bulk") {
        const start = Number(formData.bulkStart);
        const count = Number(formData.bulkCount);

        if (!start || !count || count < 1) {
          showSnackbar("Invalid bulk values", "error");
          return;
        }

        const results = await Promise.all(
          Array.from({ length: count }).map((_, i) =>
            insertRoomData({
              ...basePayload,
              room_number: start + i,
            })
          )
        );

        const failed = results.find(r => r?.status_message === "failed");

        if (failed) {
          showSnackbar(failed.message || "Bulk insert failed", "error");
          return;
        }

        if (showSnackbarWithCallback) {
          showSnackbarWithCallback(`${count} Rooms Added successfully`, "success", () => {
            onRoomAdded();
          });
          handleClose();
        } else {
          handleClose();
          onRoomAdded();
        }
        return;
      }
    } catch (err) {
      showSnackbar("Something went wrong", "error");
    }
  };

  function getBlockList(hotel_code: any) {
    getBlockData(hotel_code).then((response: any) => {
      setBlockList(response.data || []);
    });
  }

  const getfloorList = (block_id: number) => {
    return getFloorData(block_id).then((res: any) => {
      if (res?.status_message === "success") {
        setfloorList(res.data);
      }
    });
  };

  useEffect(() => {
    if (loggedUser?.hotel_code) {
      getBlockList(loggedUser?.hotel_code);
    }
  }, []);

  useEffect(() => {
    if (selectedBlockId) {
      getfloorList(selectedBlockId);
    }
  }, [selectedBlockId]);

  return (
    <div className="booking-form-wrapper">
      <div style={{
        padding: "16px 20px",
        borderBottom: "1px solid #e5e7eb",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center"
      }}>
        <h3 className="modal-title">
          {roomData?.room ? "Edit Room" : "Add Room"}
        </h3>

        <div style={{ display: "flex", gap: "10px", padding: "0 !important" }}>
          <Button className="form-top-btn" onClick={() => setMode("single")}>
            Single
          </Button>
          <Button className="form-top-btn" onClick={() => setMode("bulk")}>
            Bulk
          </Button>
        </div>
      </div>

      <Formik
        enableReinitialize
        initialValues={defaultValues}
        validationSchema={formValidation}
        onSubmit={(event) => handleSubmit(event)}
      >
        <Form className="grid lg:grid-cols-1 p-8">
          <FormGroup>
            <label className="py-2">Room Type <span style={{ color: "red" }}>*</span></label>
            <Field as="select" name="room_type_id">
              <option value="">Select</option>
              {roomTypeList.map((list: any, key: number) => (
                <option value={list.id} key={key}>
                  {list.room_type_name}
                </option>
              ))}
            </Field>
            <ErrorMessage
              name="room_type_id"
              component="div"
              className="text-red-600 text-sm mt-1"
            />
          </FormGroup>

          <div className="grid gap-4 lg:grid-cols-2">
            <FormGroup>
              <label className="py-2">Room Heading <span style={{ color: "red" }}>*</span></label>
              <Field
                name="room_heading"
                className="con-field"
                placeholder="Enter Room heading"
                type="text"
              />
              <ErrorMessage name="room_heading" component="div" className="text-red-600 text-sm mt-1" />
            </FormGroup>

            {mode === "single" && (
              <FormGroup>
                <label className="py-2">Room Number <span style={{ color: "red" }}>*</span></label>
                <Field
                  name="room_number"
                  className="con-field"
                  placeholder="Enter Room number"
                  type="number"
                />
                <ErrorMessage name="room_number" component="div" className="text-red-600 text-sm mt-1" />
              </FormGroup>
            )}

            {mode === "bulk" && (
              <>
                <FormGroup>
                  <label className="py-2">Start Room Number <span style={{ color: "red" }}>*</span></label>
                  <Field
                    name="bulkStart"
                    className="con-field"
                    type="number"
                    placeholder="Enter starting room number"
                  />
                  <ErrorMessage name="bulkStart" component="div" className="text-red-600 text-sm mt-1" />
                </FormGroup>

                <FormGroup>
                  <label className="py-2">Number of Rooms <span style={{ color: "red" }}>*</span></label>
                  <Field
                    name="bulkCount"
                    className="con-field"
                    type="number"
                    placeholder="Enter number of rooms"
                  />
                  <ErrorMessage name="bulkCount" component="div" className="text-red-600 text-sm mt-1" />
                </FormGroup>
              </>
            )}
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <FormGroup>
              <label className="py-2">Block <span style={{ color: "red" }}>*</span></label>
              <select
                className="con-field"
                value={selectedBlockId}
                onChange={(e) => setSelectedBlockId(e.target.value)}
              >
                <option value="">Select</option>
                {blockList.map((b: any) => (
                  <option key={b.id} value={b.id}>
                    {b.block_name}
                  </option>
                ))}
              </select>
            </FormGroup>

            <FormGroup>
              <label className="py-2">Floor <span style={{ color: "red" }}>*</span></label>
              <select
                className="con-field"
                value={selectedFloorId}
                onChange={(e) => setSelectedFloorId(e.target.value)}
              >
                <option value="">Select</option>
                {floorList.map((f: any) => (
                  <option key={f.id} value={f.id}>
                    {f.floor_name}
                  </option>
                ))}
              </select>
              <p className="text-red-600">
                <ErrorMessage name="floor_id" />
              </p>
            </FormGroup>
          </div>

          <div className="form-actions pt-4">
            <FormGroup>
              <Button
                type="button"
                onClick={() => handleClose()}
                className="bg-sky-300 hover:bg-sky-400"
              >
                Cancel
              </Button>
            </FormGroup>
            <FormGroup>
              <Button type="submit" className="bg-sky-300 hover:bg-sky-400">
                Save
              </Button>
            </FormGroup>
          </div>
        </Form>
      </Formik>

      <Snackbar
        visible={snackbar.visible}
        message={snackbar.message}
        type={snackbar.type}
        onClose={() => setSnackbar({ ...snackbar, visible: false })}
      />
    </div>
  );
};

export default NewAddRoomForm;